import { API_ROUTES, API_METHODS, type ApiResponse, type ApiError } from './apiRoutes';
import type { Doctor } from '@/types/doctor';
import type { DoctorStats } from '@/types/doctor-stats';
import { sendErrorToSentry, sanitizeData, sanitizeHeaders } from './sentry';

type RawPayout = {
  created_at: string;
  ID?: string;
  id?: string;
  account_id?: string;
  amount: number;
  doctor_cashback?: number;
  status: string;
  payout_interpay_id?: number;
  cancellation_reason?: string;
  payed_at?: string | null;
  prescription?: {
    id?: string;
    visible_id?: number | string;
    diagnosis?: string;
    payed?: boolean;
    released_sum?: number;
  } | null;
};

export class ClientApi {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL?: string) {
    this.baseURL = baseURL ?? '';
  }

  // Set authorization token
  setToken(token: string | null) {
    if (!token) {
      this.token = null;
      return;
    }
    // Ensure Bearer prefix if missing
    this.token = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }

  // Allow updating base URL at runtime (e.g., from runtimeConfig.public.baseURL)
  setBaseURL(baseURL?: string | null) {
    if (!baseURL) {
      return;
    }
    // Normalize trailing slash to avoid double slashes with endpoints
    this.baseURL = baseURL.replace(/\/+$/, '');
  }

  // Get authorization headers
  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    if (this.token) {
      headers.Authorization = this.token;
    }

    return headers;
  }


  // Generic request method
  private async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const headers = this.getHeaders();
    const method = options.method || 'GET';

    const config: RequestInit = {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        // Try parse JSON, then text, else empty
        let errorData: any = null;
        try {
          errorData = await response.json();
        } catch {
          try {
            const text = await response.text();
            errorData = text ? { message: text } : {};
          } catch {
            errorData = {};
          }
        }
        
        const apiError: ApiError = {
          status: response.status,
          message: (errorData && (errorData.message || errorData.error)) || `HTTP ${response.status}`,
          code: errorData?.code ?? errorData?.error_code,
          data: errorData,
        };

        // Отправляем в Sentry
        try {
          sendErrorToSentry(
            apiError,
            url,
            method,
            options.body as string,
            this.getHeaders(),
            errorData
          );
        } catch (e) {
          // Игнорируем ошибки Sentry
          console.error('[ClientApi] Ошибка при отправке в Sentry:', e);
        }

        throw apiError;
      }

      if (response.status === 204) {
        return {} as T;
      }

      try {
        return (await response.json()) as T;
      } catch {
        // Fallback to text or empty
        const text = await response.text().catch(() => '');
        return (text ? ({ message: text } as unknown as T) : ({} as T));
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error) {
        throw error;
      }
      
      const networkError: ApiError = {
        status: 500,
        message: error instanceof Error ? error.message : 'Network error or unexpected response',
        data: error,
      };

      // Отправляем network ошибку в Sentry
      try {
        sendErrorToSentry(
          networkError,
          url,
          method,
          options.body as string,
          this.getHeaders()
        );
      } catch (e) {
        // Игнорируем ошибки Sentry
        console.error('[ClientApi] Ошибка при отправке network error в Sentry:', e);
      }

      throw networkError;
    }
  }

  // Auth API methods
  async sendOtp(phoneNumber: string): Promise<ApiResponse> {
    return this.request<ApiResponse>(API_ROUTES.AUTH.SEND_OTP, {
      method: API_METHODS.POST,
      body: JSON.stringify({
        phone_number: phoneNumber.replace(/\s+/g, ''),
      }),
    });
  }

  async verifyOtp(phoneNumber: string, otp: string): Promise<ApiResponse> {
    return this.request<ApiResponse>(API_ROUTES.AUTH.VERIFY_OTP, {
      method: API_METHODS.POST,
      body: JSON.stringify({
        phone_number: phoneNumber.replace(/\s+/g, ''),
        otp,
      }),
    });
  }

  async register(
    phoneNumber: string,
    password: string,
    role: string,
    verificationToken: string
  ): Promise<ApiResponse> {
    return this.request<ApiResponse>(API_ROUTES.AUTH.REGISTER, {
      method: API_METHODS.POST,
      body: JSON.stringify({
        phone_number: phoneNumber.replace(/\s+/g, ''),
        password,
        role,
        verification_token: verificationToken,
      }),
    });
  }

  async resetPassword(
    phoneNumber: string,
    otp: string,
    newPassword: string
  ): Promise<ApiResponse> {
    return this.request<ApiResponse>(API_ROUTES.AUTH.RESET_PASSWORD, {
      method: API_METHODS.POST,
      body: JSON.stringify({
        phone_number: phoneNumber.replace(/\s+/g, ''),
        otp,
        new_password: newPassword,
      }),
    });
  }

  async saveSignature(svgBase64: string): Promise<ApiResponse> {
    return this.request<ApiResponse>(API_ROUTES.ACCOUNT.DOCTOR.SIGNATURE, {
      method: API_METHODS.POST,
      body: JSON.stringify({
        signature_svg_base64: svgBase64,
      }),
    });
  }

  async login(credentials: { phone_number: string; password?: string; role?: string }): Promise<ApiResponse> {
    console.log('login', credentials);
    return this.request<ApiResponse>(API_ROUTES.AUTH.LOGIN, {
      method: API_METHODS.POST,
      body: JSON.stringify({
        ...credentials,
        role: credentials.role || 'doctor',
      }),
    });
  }

  async logout(): Promise<ApiResponse> {
    return this.request<ApiResponse>(API_ROUTES.AUTH.LOGOUT, {
      method: API_METHODS.POST,
    });
  }

  async refreshToken(refreshToken: string): Promise<ApiResponse> {
    return this.request<ApiResponse>(API_ROUTES.AUTH.REFRESH, {
      method: API_METHODS.POST,
      body: JSON.stringify({
        refresh_token: refreshToken,
      }),
    });
  }

  // Account API methods
  async getMe(): Promise<ApiResponse> {
    return this.request<ApiResponse>(API_ROUTES.ACCOUNT.ME, {
      method: API_METHODS.GET,
    });
  }

  async updateAccount(accountId: string, clinicId: string): Promise<ApiResponse> {
    return this.request<ApiResponse>(API_ROUTES.ACCOUNT.UPDATE(accountId), {
      method: API_METHODS.PUT,
      body: JSON.stringify({
        avatar_url: null,
        clinic_id: clinicId,
        clinics: null,
        email: null,
        first_name: null,
        last_name: null,
        phone: null,
        surname: null,
      }),
    });
  }

  async findAccount(prompt: string, role: string): Promise<any[]> {
    const url = `${API_ROUTES.ACCOUNT.FIND}?prompt=${encodeURIComponent(prompt)}&role=${encodeURIComponent(role)}`;
    const response = await this.request<{ accounts: any[] }>(url, {
      method: API_METHODS.GET,
    });
    return response.accounts || [];
  }

  async searchCustomers(prompt: string): Promise<any[]> {
    const searchQuery = prompt.replace(/^\+/, '');
    const url = `${API_ROUTES.CUSTOMER.LIST}?search_query=${encodeURIComponent(searchQuery)}`;
    const response = await this.request<{ result: { items: any[] } }>(url, {
      method: API_METHODS.GET,
    });
    const items = response?.result?.items || [];
    return items.map((item: any) => ({ ...item, phone: item.phone_number }));
  }

  async getDoctorStats(): Promise<{ stats: DoctorStats }> {
    const response = await this.request<{ result: DoctorStats }>(API_ROUTES.ACCOUNT.DOCTOR.STATS, {
      method: API_METHODS.GET,
    });
    const data = response.result ?? (response as unknown as DoctorStats);
    return {
      stats: {
        total_prescriptions_amount: data.total_prescriptions_amount,
        total_paid_prescriptions_amount: data.total_paid_prescriptions_amount,
        total_released_sum: data.total_released_sum,
        total_cashback: data.total_cashback,
        current_cashback: data.current_cashback,
      },
    };
  }

  async getDoctorById(accountId: string): Promise<{ doctor: Doctor }> {
    const response = await this.request<Doctor>(API_ROUTES.ACCOUNT.DOCTOR.BY_ID(accountId), {
      method: API_METHODS.GET,
    });
    return {
      doctor: {
        id: response.id,
        account_id: response.account_id,
        signature: response.signature,
        job_title: response.job_title,
        created_at: response.created_at,
        clinics: response.clinics,
      },
    };
  }

  // Product API methods
  async findProducts(prompt: string, city: string): Promise<any[]> {
    const url = `${API_ROUTES.PRODUCT.SEARCH}?page=1&size=10&prompt=${encodeURIComponent(prompt)}&city=${encodeURIComponent(city)}`;
    const response = await this.request<any>(url, {
      method: API_METHODS.GET,
    });
    const products = (response.result?.products ?? response.response?.products ?? response.products) || [];
    return products.map((p: any) => ({ ...p, id: p.id ?? p.sku, brand_name: p.brand_name ?? p.brand ?? '' }));
  }

  // Prescription API methods
  async createPrescription(prescriptionData: {
    client_id: string | null;
    client_phone?: string;
    doctor_id?: string;
    clinic_id?: string;
    diagnosis: string;
    description: string;
    product_ids_count: Record<string, number>;
    selected_products_data?: any[];
  }): Promise<ApiResponse> {
    const data = {
      client_id: prescriptionData.client_id,
      client_phone: prescriptionData.client_phone || '',
      doctor_id: prescriptionData.doctor_id || '',
      clinic_id: prescriptionData.clinic_id || '',
      status: 'active',
      description: prescriptionData.description,
      diagnosis: prescriptionData.diagnosis,
      product_ids_count: prescriptionData.product_ids_count,
    };

    const response = await this.request<any>(API_ROUTES.PRESCRIPTION.CREATE, {
      method: API_METHODS.POST,
      body: JSON.stringify(data),
    });

    // Enrich response with products data
    let products = null;
    if (prescriptionData.selected_products_data && Array.isArray(prescriptionData.selected_products_data) && prescriptionData.selected_products_data.length > 0) {
      products = prescriptionData.selected_products_data.map((product: any) => ({
        id: product.id,
        name: product.name,
        brand_name: product.brand_name,
        quantity: product.quantity || prescriptionData.product_ids_count[product.id] || 1
      }));
    } else if (prescriptionData.product_ids_count && Object.keys(prescriptionData.product_ids_count).length > 0) {
      products = Object.entries(prescriptionData.product_ids_count).map(([productId, quantity]) => ({
        id: productId,
        name: `Product ${productId}`,
        brand_name: '',
        quantity: quantity
      }));
    }

    return {
      success: true,
      message: 'Prescription created successfully',
      data: {
        ...response,
        products: products
      },
    };
  }

  async getPrescriptions(params: {
    client_id?: string;
    page?: number;
    limit?: number;
    desc?: string;
    order_by?: string;
    doctor_id?: string;
    client_phone?: string;
    search?: string;
    visible_id?: string;
    date_from?: string;
    date_to?: string;
    status?: string;
  }): Promise<{
    success: boolean;
    prescriptions: any[];
    total: number;
    page: number;
  }> {
    const searchParams = new URLSearchParams();
    if (params.page != null) searchParams.append('page', String(params.page));
    if (params.limit != null) searchParams.append('limit', String(params.limit));
    if (params.order_by) searchParams.append('order_by', params.order_by);
    if (params.desc) searchParams.append('desc', params.desc);
    if (params.client_id) searchParams.append('client_id', params.client_id);
    if (params.doctor_id) searchParams.append('doctor_id', params.doctor_id);
    if (params.client_phone) searchParams.append('client_phone', params.client_phone);
    if (params.search) searchParams.append('search', params.search);
    if (params.visible_id) searchParams.append('visible_id', params.visible_id);
    if (params.date_from) searchParams.append('date_from', params.date_from);
    if (params.date_to) searchParams.append('date_to', params.date_to);
    if (params.status) searchParams.append('status', params.status);

    const url = `${API_ROUTES.PRESCRIPTION.GET_ALL}?${searchParams.toString()}`;
    const response = await this.request<any>(url, {
      method: API_METHODS.GET,
    });

    console.log('[getPrescriptions] raw response keys:', response && typeof response === 'object' ? Object.keys(response) : typeof response);

    let prescriptionsArray: any[] = [];
    if (Array.isArray(response)) {
      prescriptionsArray = response;
    } else if (Array.isArray(response?.result?.items)) {
      prescriptionsArray = response.result.items;
    } else if (Array.isArray(response?.result?.prescriptions)) {
      prescriptionsArray = response.result.prescriptions;
    } else if (Array.isArray(response?.prescriptions)) {
      prescriptionsArray = response.prescriptions;
    } else if (Array.isArray(response?.data)) {
      prescriptionsArray = response.data;
    }

    return {
      success: true,
      prescriptions: prescriptionsArray,
      total: response?.result?.total ?? response.total ?? 0,
      page: response?.result?.page ?? response.page ?? 1,
    };
  }

  async getPrescriptionById(id: string): Promise<Record<string, unknown>> {
    const response = await this.request<{ result: Record<string, unknown> }>(
      API_ROUTES.PRESCRIPTION.GET_BY_ID(id),
      { method: API_METHODS.GET }
    );
    return response?.result ?? (response as unknown as Record<string, unknown>);
  }

  // Payment API methods
  async getPaymentCards(storeNetId?: string): Promise<any> {
    const url = storeNetId 
      ? `${API_ROUTES.PAYMENT.CARD.GET}?store_net_id=${encodeURIComponent(storeNetId)}`
      : API_ROUTES.PAYMENT.CARD.GET;
    
    return this.request<any>(url, {
      method: API_METHODS.GET,
    });
  }

  async createPaymentCard(_data?: {
    language?: string;
    store_net_id?: string;
  }): Promise<any> {
    // Ensure we send useful defaults from frontend
    const payload = {
      ...(typeof _data === 'object' ? _data : {}),
    } as Record<string, any>;

    try {
      return await this.request<any>(API_ROUTES.PAYMENT.CARD.POST, {
        method: API_METHODS.POST,
        body: JSON.stringify(payload),
      });
    } catch (err: any) {
      throw err;
    }
  }

  async deletePaymentCard(data: {
    external_id: string;
    store_net_id?: string;
  }): Promise<any> {
    const params = new URLSearchParams({
      external_id: data.external_id,
    });

    if (data.store_net_id) {
      params.append('store_net_id', data.store_net_id);
    }

    return this.request<any>(`${API_ROUTES.PAYMENT.CARD.DELETE}?${params.toString()}`, {
      method: API_METHODS.DELETE,
    });
  }

  async getPrescriptionPayouts(): Promise<{ payouts: RawPayout[] }> {
    const response = await this.request<{ result?: { payouts?: RawPayout[] }; payouts?: RawPayout[] }>(API_ROUTES.PAYMENT.PRESCRIPTIONS.GET, {
      method: API_METHODS.GET,
    });

    const payouts = response?.result?.payouts ?? response?.payouts;
    return {
      payouts: Array.isArray(payouts) ? payouts : [],
    };
  }

  async getPayouts(): Promise<{ payouts: RawPayout[] }> {
    const response = await this.request<{ payouts?: RawPayout[] }>(`${API_ROUTES.PAYMENT.PAYOUT.GET}?page=1&limit=20`, {
      method: API_METHODS.GET,
    });

    return {
      payouts: Array.isArray(response?.payouts) ? response.payouts : [],
    };
  }

  // News API methods
  async getNews(): Promise<any> {
    return this.request<any>(API_ROUTES.NEWS.GET, {
      method: API_METHODS.GET,
    });
  }
}

// Create singleton instance
export const clientApi = new ClientApi();
